import React, { useEffect, useState, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';

const formatDateShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${m}-${day} ${h}:${min}`;
};

const QueueItem = memo(({ it, t, API_BASE }) => {
  const jn = String(it.job_name||'');
  const jl = (jn==='separate_audio') ? t('jobs.separateAudio') : (jn==='denoise' ? t('jobs.denoise') : (jn==='2Dconv3D' ? t('jobs.conv3d') : (jn==='pic2dance' ? t('jobs.pic2dance') : jn)));
  const dateStr = formatDateShort(it.file_in_date);
  
  const opts = it.file_in_options || {};
  const points = (Number(opts.useSub) || 0) + (Number(opts.useExt) || 0);
  const videoName = opts.video_org_name ? String(opts.video_org_name) : (opts.video ? String(opts.video) : '');

  let statusNode = null;
  const inProg = Number(it.in_progress || 0);

  if (inProg < 0) {
    statusNode = <div className="text-right whitespace-nowrap text-red-600">{t('queue.cancelled')}</div>;
  } else if (it.downloadable) {
    statusNode = (
      <div className="text-right">
        <a href={`${API_BASE}/api/auth/queue/download-out?serial=${it.serial}`} className="text-blue-700 hover:underline">{t('queue.download')}</a>
      </div>
    );
  } else if (it.expired) {
    statusNode = <div className="text-right text-gray-500">{t('queue.expired')}</div>;
  } else {
    statusNode = (
      <div className="text-right text-gray-600">
        {inProg < 1 ? t('queue.waiting') : `${inProg}%`}
      </div>
    );
  }

  return (
    <div className="flex flex-col text-sm border rounded px-3 py-2">
      <div className="flex justify-between w-full">
        <div className="font-semibold text-gray-700 truncate mr-2">
          {jl}
        </div>
        <div className="whitespace-nowrap">
          {statusNode}
        </div>
      </div>

      <div className="flex flex-wrap items-center text-xs text-gray-500 mt-1 gap-x-3">
        <span>{dateStr}</span>
        <span>{t('queue.consumedPoints', { points })}</span>
        <span className="truncate max-w-[200px]" title={it.file_in_name_org || it.file_in_name}>
           {it.file_in_name_org || it.file_in_name}
        </span>
        {videoName && (
          <span className="truncate max-w-[150px]" title={videoName}>
            + {videoName}
          </span>
        )}
      </div>

      {inProg < 0 && it.process_msg && (
        <div className="text-right text-red-600 text-xs mt-1">
          {it.process_msg}
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  const p = prev.it;
  const n = next.it;
  return p.serial === n.serial &&
         p.in_progress === n.in_progress &&
         p.process_msg === n.process_msg &&
         p.downloadable === n.downloadable &&
         p.expired === n.expired &&
         p.file_out_name === n.file_out_name &&
         p.file_in_date === n.file_in_date &&
         JSON.stringify(p.file_in_options) === JSON.stringify(n.file_in_options);
});

export default function QueueModal({ onClose }) {
  const { t } = useTranslation();
  const [queueItems, setQueueItems] = useState([]);
  const isFetching = useRef(false);

  // API base: prefer SERVER_URL from .env, else default to current host with port 4000
  const API_BASE = String(process.env.REACT_APP_SERVER_URL || process.env.SERVER_URL || '').trim() || `${window.location.protocol}//${window.location.hostname}:4000`;

  useEffect(() => {
    let interval = null;
    const fetchQueue = async () => {
      if (isFetching.current) return;
      isFetching.current = true;
      try {
        const resp = await fetch(`${API_BASE}/api/auth/queue/list?limit=50&_t=${Date.now()}`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          if (data && Array.isArray(data.items)) {
            setQueueItems(prev => {
              if (!prev || prev.length === 0) return data.items;
              
              const newItems = data.items.map(newItem => {
                const oldItem = prev.find(p => p.serial === newItem.serial);
                if (oldItem) {
                   if (
                     oldItem.in_progress === newItem.in_progress &&
                     oldItem.process_msg === newItem.process_msg &&
                     oldItem.downloadable === newItem.downloadable &&
                     oldItem.expired === newItem.expired &&
                     oldItem.file_out_name === newItem.file_out_name &&
                     oldItem.file_in_date === newItem.file_in_date &&
                     JSON.stringify(oldItem.file_in_options) === JSON.stringify(newItem.file_in_options)
                   ) {
                     return oldItem;
                   }
                }
                return newItem;
              });

              if (newItems.length === prev.length && newItems.every((it, i) => it === prev[i])) {
                return prev;
              }
              return newItems;
            });
          }
        }
      } catch (e) {
        console.warn('Queue fetch failed', e);
      } finally {
        isFetching.current = false;
      }
    };

    fetchQueue();
    interval = setInterval(fetchQueue, 3000);

    return () => {
      if (interval) clearInterval(interval);
      isFetching.current = false;
    };
  }, [API_BASE]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000]">
      <div className="bg-white rounded-lg p-8 w-[720px] text-gray-900">
        <div className="text-lg font-semibold mb-4">{t('queue.status')}</div>
        <div className="space-y-2 max-h-[420px] overflow-auto">
          {(queueItems && queueItems.length > 0) ? (
            queueItems.map((it) => (
              <QueueItem 
                key={it.serial} 
                it={it} 
                t={t} 
                API_BASE={API_BASE} 
              />
            ))
          ) : (
            <div className="text-sm text-gray-700">{t('common.noItems')}</div>
          )}
        </div>
        <div className="text-center mt-6">
          <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
}
